import React from 'react';
import { Link } from 'react-router-dom';

const Services: React.FC = () => {
  return (
    <main className="services-page">
      {/* Hero with Animated Data Visualization */}
      <section className="services-hero-v2">
        {/* Background Elements */}
        <div className="hero-bg-elements">
          {/* Animated grid */}
          <div className="hero-grid"></div>
          
          {/* Floating data points */}
          <div className="hero-dots">
            <div className="hero-dot" style={{ '--x': '10%', '--y': '30%', '--delay': '0s' } as React.CSSProperties}></div>
            <div className="hero-dot" style={{ '--x': '85%', '--y': '25%', '--delay': '0.5s' } as React.CSSProperties}></div>
            <div className="hero-dot" style={{ '--x': '20%', '--y': '70%', '--delay': '1s' } as React.CSSProperties}></div>
            <div className="hero-dot" style={{ '--x': '75%', '--y': '65%', '--delay': '1.5s' } as React.CSSProperties}></div>
            <div className="hero-dot" style={{ '--x': '50%', '--y': '20%', '--delay': '2s' } as React.CSSProperties}></div>
            <div className="hero-dot" style={{ '--x': '90%', '--y': '80%', '--delay': '2.5s' } as React.CSSProperties}></div>
          </div>
          
          {/* Background chart silhouette */}
          <svg className="hero-chart-bg" viewBox="0 0 400 200" preserveAspectRatio="none">
            <polyline className="hero-chart-line" points="0,180 50,150 100,160 150,120 200,130 250,90 300,100 350,60 400,40" />
            <polygon className="hero-chart-fill" points="0,180 50,150 100,160 150,120 200,130 250,90 300,100 350,60 400,40 400,200 0,200" />
          </svg>
        </div>
        
        <div className="container hero-content">
          <span className="services-label">Our Services</span>
          <h1>Everything you need to operate with better data</h1>
          <p>
            From building your data foundation to ongoing support — 
            structured offerings for every stage of growth.
          </p>
          
          {/* Mini stats row */}
          <div className="hero-stats">
            <div className="hero-stat">
              <span className="stat-number">5</span>
              <span className="stat-label">Service Packages</span>
            </div>
            <div className="hero-stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">Custom Solutions</span>
            </div>
            <div className="hero-stat">
              <span className="stat-number">∞</span>
              <span className="stat-label">Ongoing Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* What We Help You Do - Numbered Cards */}
      <section className="section outcomes-section">
        <div className="container">
          <div className="outcomes-header">
            <span className="section-label">Clear Outcomes</span>
            <h2>What we help you do</h2>
            <p>Results you can measure, not technical jargon.</p>
          </div>
          
          <div className="outcomes-grid">
            <div className="outcome-card" data-outcome="organize">
              <div className="outcome-index">01</div>
              <div className="outcome-visual">
                <svg viewBox="0 0 50 50" fill="none">
                  <rect x="5" y="30" width="10" height="15" className="block block-1"/>
                  <rect x="20" y="20" width="10" height="25" className="block block-2"/>
                  <rect x="35" y="10" width="10" height="35" className="block block-3"/>
                </svg>
              </div>
              <div className="outcome-content">
                <h4>Get your data organized</h4>
                <div className="outcome-line"><span></span></div>
              </div>
            </div>
            
            <div className="outcome-card" data-outcome="performance">
              <div className="outcome-index">02</div>
              <div className="outcome-visual">
                <svg viewBox="0 0 50 50" fill="none">
                  <circle cx="25" cy="25" r="18" className="gauge-track"/>
                  <circle cx="25" cy="25" r="18" className="gauge-fill"/>
                  <line x1="25" y1="25" x2="25" y2="12" className="gauge-needle"/>
                </svg>
              </div>
              <div className="outcome-content">
                <h4>Understand your performance</h4>
                <div className="outcome-line"><span></span></div>
              </div>
            </div>
            
            <div className="outcome-card" data-outcome="forecast">
              <div className="outcome-index">03</div>
              <div className="outcome-visual">
                <svg viewBox="0 0 50 50" fill="none">
                  <polyline className="trend-solid" points="5,40 20,30 30,32 40,20"/>
                  <polyline className="trend-dashed" points="40,20 48,12"/>
                  <circle cx="40" cy="20" r="3" className="trend-point"/>
                </svg>
              </div>
              <div className="outcome-content">
                <h4>Forecast growth</h4>
                <div className="outcome-line"><span></span></div>
              </div>
            </div>
            
            <div className="outcome-card" data-outcome="automate">
              <div className="outcome-index">04</div>
              <div className="outcome-visual">
                <svg viewBox="0 0 50 50" fill="none">
                  <circle cx="25" cy="25" r="12" className="gear-outer"/>
                  <circle cx="25" cy="25" r="5" className="gear-inner"/>
                  <line x1="25" y1="8" x2="25" y2="13" className="gear-tooth"/>
                  <line x1="25" y1="37" x2="25" y2="42" className="gear-tooth"/>
                  <line x1="8" y1="25" x2="13" y2="25" className="gear-tooth"/>
                  <line x1="37" y1="25" x2="42" y2="25" className="gear-tooth"/>
                </svg>
              </div>
              <div className="outcome-content">
                <h4>Automate manual work</h4>
                <div className="outcome-line"><span></span></div>
              </div>
            </div>
            
            <div className="outcome-card" data-outcome="decide">
              <div className="outcome-index">05</div>
              <div className="outcome-visual">
                <svg viewBox="0 0 50 50" fill="none">
                  <polyline className="check-mark" points="12,25 22,35 38,15"/>
                  <circle cx="25" cy="25" r="20" className="check-circle"/>
                </svg>
              </div>
              <div className="outcome-content">
                <h4>Make better decisions</h4>
                <div className="outcome-line"><span></span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Packages with Animated Visuals */}
      <section className="section packages-section" id="packages">
        {/* Background data visualization */}
        <div className="packages-bg">
          <div className="packages-bg-grid"></div>
          <div className="packages-bg-dots">
            <div className="pkg-dot" style={{ '--x': '5%', '--y': '20%', '--delay': '0s' } as React.CSSProperties}></div>
            <div className="pkg-dot" style={{ '--x': '95%', '--y': '40%', '--delay': '1s' } as React.CSSProperties}></div>
            <div className="pkg-dot" style={{ '--x': '10%', '--y': '80%', '--delay': '2s' } as React.CSSProperties}></div>
            <div className="pkg-dot" style={{ '--x': '92%', '--y': '85%', '--delay': '3s' } as React.CSSProperties}></div>
          </div>
        </div>
        
        <div className="container packages-content">
          <div className="packages-header-v2">
            <span className="section-label">Service Packages</span>
            <h2>Structured solutions for every stage</h2>
            <p>
              Start where you need to, grow from there. Each package delivers 
              clear business outcomes.
            </p>
          </div>

          <div className="packages-grid-v2">
            {/* Foundation */}
            <div className="package-v2" id="foundation">
              <div className="package-v2-visual">
                <div className="package-visual-bg"></div>
                <svg viewBox="0 0 120 120" fill="none" className="package-svg">
                  {/* Stacking blocks animation */}
                  <rect x="20" y="80" width="80" height="20" rx="4" className="foundation-block fb-1"/>
                  <rect x="30" y="55" width="60" height="20" rx="4" className="foundation-block fb-2"/>
                  <rect x="40" y="30" width="40" height="20" rx="4" className="foundation-block fb-3"/>
                  {/* Connection lines */}
                  <line x1="60" y1="75" x2="60" y2="55" className="foundation-line"/>
                  <line x1="60" y1="50" x2="60" y2="30" className="foundation-line"/>
                </svg>
                <span className="package-v2-badge">Foundation</span>
              </div>
              
              <div className="package-v2-content">
                <h3>Data Foundation Setup</h3>
                <p className="package-v2-tagline">"Get your data organized and reliable"</p>
                
                <ul className="package-v2-features">
                  <li>Centralized data setup (warehouse or unified database)</li>
                  <li>Connect key systems: payments, CRM, marketing, product</li>
                  <li>Automated daily data updates</li>
                  <li>Clean schemas and naming conventions</li>
                  <li>Basic data documentation</li>
                </ul>

                <div className="package-v2-outcome">
                  <span className="outcome-label">Business Outcome</span>
                  <p>One source of truth. No more spreadsheets stitched together manually.</p>
                </div>

                <div className="package-v2-footer">
                  <span className="best-for">Best for: Companies with data scattered across systems</span>
                  <Link to="/contact" className="package-v2-cta">
                    Get Started <span>→</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Visibility */}
            <div className="package-v2" id="visibility">
              <div className="package-v2-visual">
                <div className="package-visual-bg"></div>
                <svg viewBox="0 0 120 120" fill="none" className="package-svg">
                  {/* Line chart with animated data points */}
                  <polyline className="visibility-line" points="15,85 35,65 55,70 75,45 95,50 105,30"/>
                  <circle cx="15" cy="85" r="5" className="visibility-dot vd-1"/>
                  <circle cx="35" cy="65" r="5" className="visibility-dot vd-2"/>
                  <circle cx="55" cy="70" r="5" className="visibility-dot vd-3"/>
                  <circle cx="75" cy="45" r="5" className="visibility-dot vd-4"/>
                  <circle cx="95" cy="50" r="5" className="visibility-dot vd-5"/>
                  <circle cx="105" cy="30" r="5" className="visibility-dot vd-6"/>
                  {/* Baseline */}
                  <line x1="10" y1="95" x2="110" y2="95" className="visibility-baseline"/>
                </svg>
                <span className="package-v2-badge green">Visibility</span>
              </div>
              
              <div className="package-v2-content">
                <h3>Dashboards & Business Visibility</h3>
                <p className="package-v2-tagline">"Know what's happening without digging"</p>
                
                <ul className="package-v2-features">
                  <li>Executive dashboards with key metrics</li>
                  <li>Department-level dashboards (sales, marketing, ops)</li>
                  <li>Clear KPI definitions</li>
                  <li>Automated reporting & alerts</li>
                  <li>Historical trend views</li>
                </ul>

                <div className="package-v2-outcome">
                  <span className="outcome-label">Business Outcome</span>
                  <p>Faster decisions. Fewer blind spots. Less time pulling reports.</p>
                </div>

                <div className="package-v2-footer">
                  <span className="best-for">Best for: Teams spending hours building reports</span>
                  <Link to="/contact" className="package-v2-cta">
                    Get Started <span>→</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Forecasting */}
            <div className="package-v2" id="forecasting">
              <div className="package-v2-visual">
                <div className="package-visual-bg"></div>
                <svg viewBox="0 0 120 120" fill="none" className="package-svg">
                  {/* Solid trend line (past) */}
                  <polyline className="forecast-past" points="10,90 30,75 50,70 70,55"/>
                  {/* Dashed forecast line (future) */}
                  <polyline className="forecast-future" points="70,55 90,40 110,25"/>
                  {/* Divider line */}
                  <line x1="70" y1="15" x2="70" y2="100" className="forecast-divider"/>
                  {/* Labels */}
                  <text x="35" y="108" className="forecast-label">Past</text>
                  <text x="85" y="108" className="forecast-label">Future</text>
                  {/* Confidence band */}
                  <polygon className="forecast-band" points="70,55 90,35 110,18 110,32 90,45 70,55"/>
                </svg>
                <span className="package-v2-badge navy">Forecasting</span>
              </div>
              
              <div className="package-v2-content">
                <h3>Forecasting & Planning</h3>
                <p className="package-v2-tagline">"See what's likely to happen next"</p>
                
                <ul className="package-v2-features">
                  <li>Revenue and demand forecasting</li>
                  <li>Growth projections</li>
                  <li>Capacity planning</li>
                  <li>Scenario modeling</li>
                  <li>Simple predictive models</li>
                </ul>

                <div className="package-v2-outcome">
                  <span className="outcome-label">Business Outcome</span>
                  <p>Better hiring and budgeting decisions. Fewer surprises.</p>
                </div>

                <div className="package-v2-footer">
                  <span className="best-for">Best for: Companies ready for proactive planning</span>
                  <Link to="/contact" className="package-v2-cta">
                    Get Started <span>→</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Automation */}
            <div className="package-v2" id="automation">
              <div className="package-v2-visual">
                <div className="package-visual-bg"></div>
                <svg viewBox="0 0 120 120" fill="none" className="package-svg">
                  {/* Input nodes (left) */}
                  <circle cx="25" cy="35" r="8" className="auto-node"/>
                  <circle cx="25" cy="85" r="8" className="auto-node"/>
                  
                  {/* Center processing node */}
                  <circle cx="60" cy="60" r="12" className="auto-node-center"/>
                  <circle cx="60" cy="60" r="12" className="auto-pulse ap-1"/>
                  <circle cx="60" cy="60" r="12" className="auto-pulse ap-2"/>
                  <circle cx="60" cy="60" r="12" className="auto-pulse ap-3"/>
                  
                  {/* Output nodes (right) */}
                  <circle cx="95" cy="35" r="8" className="auto-node"/>
                  <circle cx="95" cy="60" r="8" className="auto-node"/>
                  <circle cx="95" cy="85" r="8" className="auto-node"/>
                  
                  {/* Connectors */}
                  <line x1="33" y1="35" x2="48" y2="55" className="auto-connector"/>
                  <line x1="33" y1="85" x2="48" y2="65" className="auto-connector"/>
                  <line x1="72" y1="55" x2="87" y2="35" className="auto-connector"/>
                  <line x1="72" y1="60" x2="87" y2="60" className="auto-connector"/>
                  <line x1="72" y1="65" x2="87" y2="85" className="auto-connector"/>
                  
                  {/* Flowing dots */}
                  <circle r="4" className="auto-flow-dot afd-1"/>
                  <circle r="4" className="auto-flow-dot afd-2"/>
                  <circle r="4" className="auto-flow-dot afd-3"/>
                </svg>
                <span className="package-v2-badge yellow">Automation</span>
              </div>
              
              <div className="package-v2-content">
                <h3>Automation & AI Assistants</h3>
                <p className="package-v2-tagline">"Reduce manual work using automation and AI"</p>
                
                <ul className="package-v2-features">
                  <li>Automated data workflows</li>
                  <li>AI-powered internal tools</li>
                  <li>Reporting bots & document processing</li>
                  <li>Customer message routing</li>
                  <li>Knowledge assistants for teams</li>
                </ul>

                <div className="package-v2-outcome">
                  <span className="outcome-label">Business Outcome</span>
                  <p>Less manual work. Faster operations. Consistency at scale.</p>
                </div>

                <div className="package-v2-footer">
                  <span className="best-for">Best for: Teams doing repetitive data work</span>
                  <Link to="/contact" className="package-v2-cta">
                    Get Started <span>→</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Ongoing Support */}
            <div className="package-v2" id="support">
              <div className="package-v2-visual">
                <div className="package-visual-bg"></div>
                <svg viewBox="0 0 120 120" fill="none" className="package-svg">
                  {/* Outer rotating ring */}
                  <circle cx="60" cy="60" r="45" className="support-ring-track"/>
                  <circle cx="60" cy="60" r="45" className="support-ring-progress"/>
                  
                  {/* Inner cycle arrows */}
                  <g className="support-cycle">
                    <path d="M60 25 A35 35 0 0 1 95 60" className="support-arc sa-1"/>
                    <path d="M95 60 A35 35 0 0 1 60 95" className="support-arc sa-2"/>
                    <path d="M60 95 A35 35 0 0 1 25 60" className="support-arc sa-3"/>
                    <path d="M25 60 A35 35 0 0 1 60 25" className="support-arc sa-4"/>
                  </g>
                  
                  {/* Arrow heads at cardinal points */}
                  <polygon points="60,20 55,28 65,28" className="support-arrow-head sah-1"/>
                  <polygon points="100,60 92,55 92,65" className="support-arrow-head sah-2"/>
                  <polygon points="60,100 65,92 55,92" className="support-arrow-head sah-3"/>
                  <polygon points="20,60 28,65 28,55" className="support-arrow-head sah-4"/>
                  
                  {/* Center hub */}
                  <circle cx="60" cy="60" r="15" className="support-hub"/>
                  <circle cx="60" cy="60" r="8" className="support-hub-inner"/>
                  
                  {/* Heartbeat line */}
                  <polyline className="support-heartbeat" points="45,60 52,60 55,50 60,70 65,55 68,60 75,60"/>
                  
                  {/* Orbiting dot */}
                  <circle r="5" className="support-orbit-dot"/>
                </svg>
                <span className="package-v2-badge">Ongoing</span>
              </div>
              
              <div className="package-v2-content">
                <h3>Ongoing Data Support</h3>
                <p className="package-v2-tagline">"Data help without hiring full-time"</p>
                
                <ul className="package-v2-features">
                  <li>Ongoing maintenance of data systems</li>
                  <li>Dashboard updates and new metrics</li>
                  <li>Model updates and improvements</li>
                  <li>Data fixes and troubleshooting</li>
                  <li>On-call analytics help</li>
                </ul>

                <div className="package-v2-outcome">
                  <span className="outcome-label">Business Outcome</span>
                  <p>Stability. Continuous improvement. Predictable cost.</p>
                </div>

                <div className="package-v2-footer">
                  <span className="best-for">Best for: Companies wanting ongoing capability</span>
                  <Link to="/contact" className="package-v2-cta">
                    Get Started <span>→</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add-on Section with Animated Background */}
      <section className="section addon-section-v2">
        <div className="container">
          <div className="addon-card-v2">
            {/* Animated background */}
            <div className="addon-bg">
              <div className="addon-particles">
                <div className="addon-particle" style={{ '--x': '10%', '--y': '20%', '--delay': '0s' } as React.CSSProperties}></div>
                <div className="addon-particle" style={{ '--x': '90%', '--y': '30%', '--delay': '0.5s' } as React.CSSProperties}></div>
                <div className="addon-particle" style={{ '--x': '20%', '--y': '80%', '--delay': '1s' } as React.CSSProperties}></div>
                <div className="addon-particle" style={{ '--x': '80%', '--y': '70%', '--delay': '1.5s' } as React.CSSProperties}></div>
                <div className="addon-particle" style={{ '--x': '50%', '--y': '50%', '--delay': '2s' } as React.CSSProperties}></div>
              </div>
              <div className="addon-glow"></div>
            </div>
            
            <div className="addon-content">
              <span className="addon-label">Add-on Package</span>
              <h3>Experiments & Optimization</h3>
              <p className="addon-tagline">"Test what actually works before scaling"</p>
              
              <div className="addon-features-v2">
                <div className="addon-feature-v2">
                  <div className="addon-feature-icon-v2">
                    <svg viewBox="0 0 40 40" fill="none">
                      <path className="flask-body" d="M15 8V18L5 35H35L25 18V8"/>
                      <line x1="12" y1="8" x2="28" y2="8" className="flask-top"/>
                      <ellipse cx="20" cy="28" rx="8" ry="4" className="flask-liquid"/>
                    </svg>
                  </div>
                  <span>A/B Testing</span>
                </div>
                <div className="addon-feature-v2">
                  <div className="addon-feature-icon-v2">
                    <svg viewBox="0 0 40 40" fill="none">
                      <polyline className="chart-up" points="5,30 15,20 25,25 35,10"/>
                      <circle cx="35" cy="10" r="3" className="chart-point"/>
                    </svg>
                  </div>
                  <span>Conversion Analysis</span>
                </div>
                <div className="addon-feature-v2">
                  <div className="addon-feature-icon-v2">
                    <svg viewBox="0 0 40 40" fill="none">
                      <circle cx="20" cy="20" r="15" className="target-outer"/>
                      <circle cx="20" cy="20" r="10" className="target-mid"/>
                      <circle cx="20" cy="20" r="5" className="target-inner"/>
                      <circle cx="20" cy="20" r="2" className="target-center"/>
                    </svg>
                  </div>
                  <span>Funnel Optimization</span>
                </div>
                <div className="addon-feature-v2">
                  <div className="addon-feature-icon-v2">
                    <svg viewBox="0 0 40 40" fill="none">
                      <rect x="8" y="15" width="10" height="18" rx="2" className="price-tag"/>
                      <rect x="22" y="10" width="10" height="23" rx="2" className="price-tag-alt"/>
                      <path d="M18 20 L22 20" className="price-compare"/>
                    </svg>
                  </div>
                  <span>Pricing Experiments</span>
                </div>
              </div>

              <p className="addon-note-v2">
                Available for companies with mature data foundations looking to optimize.
              </p>
              
              <Link to="/contact" className="addon-cta">
                Learn More <span>→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Background Animation */}
      <section className="section services-cta-section">
        <div className="cta-bg-animation">
          <svg className="cta-wave" viewBox="0 0 1440 200" preserveAspectRatio="none">
            <path className="cta-wave-path" d="M0,100 C320,180 420,20 720,100 C1020,180 1120,20 1440,100 L1440,200 L0,200 Z"/>
          </svg>
        </div>
        <div className="container">
          <div className="services-cta-inner">
            <h2>Not sure where to start?</h2>
            <p>
              Book a free discovery call. We'll assess your current situation and 
              recommend the right starting point for your business.
            </p>
            <Link to="/contact" className="btn btn-glow btn-lg">
              Schedule a Call
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Services;
